import type { CertificateWorkspaceStatus } from '@/types/pathways';

export type AssessmentProgressStatus = CertificateWorkspaceStatus;

export interface AssessmentProgressSubmission {
  id?: string | null;
  template_id?: string | null;
  step_id?: string | null;
  course_id?: string | null;
  status?: string | null;
  score_percent?: number | null;
  completed_at?: string | null;
  graded_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CourseAssessmentProgressItem {
  id: string;
  stepId: string;
  templateId: string;
  dashboardTabId: string;
  required: boolean;
  status: AssessmentProgressStatus;
  latestSubmission: AssessmentProgressSubmission | null;
  attemptsRemaining?: number | null;
  settings?: Record<string, unknown> | null;
  embeddedWorksheet?: boolean;
}

export interface CourseAssessmentTabProgress {
  tabId: string;
  total: number;
  completed: number;
  requiredTarget: number;
  isRequirementMet: boolean;
  percentage: number;
  status: AssessmentProgressStatus | 'untracked';
  statusCounts: Record<AssessmentProgressStatus, number>;
  items: CourseAssessmentProgressItem[];
}

export interface CourseAssessmentStepProgress {
  stepId: string;
  total: number;
  completed: number;
  availableTotal: number;
  availableCompleted: number;
  requiredTarget: number;
  isRequirementMet: boolean;
  percentage: number;
  status: AssessmentProgressStatus | 'untracked';
  statusCounts: Record<AssessmentProgressStatus, number>;
  items: CourseAssessmentProgressItem[];
  tabs: Record<string, CourseAssessmentTabProgress>;
}

export interface CourseAssessmentRoadmapProgress {
  roadmapId: string;
  total: number;
  completed: number;
  percentage: number;
  statusCounts: Record<AssessmentProgressStatus, number>;
  steps: Record<string, CourseAssessmentStepProgress>;
}

export const assessmentStatusCopy: Record<AssessmentProgressStatus, { label: string; className: string }> = {
  not_started: { label: 'Not started', className: 'border-cw bg-cw-subtle text-slate-700' },
  enrolled: { label: 'Enrolled', className: 'border-studio-200 bg-studio-50 text-studio-900' },
  in_progress: { label: 'In progress', className: 'border-studio-200 bg-studio-50 text-studio-900' },
  submitted: { label: 'Submitted', className: 'border-amber-100 bg-amber-50 text-amber-700' },
  in_review: { label: 'Out for review', className: 'border-studio-200 bg-studio-50 text-studio-900' },
  needs_revision: { label: 'Needs revision', className: 'border-orange-100 bg-orange-50 text-orange-700' },
  failed: { label: 'Did not pass', className: 'border-red-100 bg-red-50 text-red-700' },
  earned: { label: 'Passed', className: 'border-emerald-100 bg-emerald-50 text-emerald-700' },
};

export function getAssessmentStatusCopy(
  status: AssessmentProgressStatus,
  options: { completionOnly?: boolean } = {},
) {
  if (options.completionOnly === true) {
    if (status === 'earned') {
      return { ...assessmentStatusCopy.earned, label: 'Completed' };
    }
    if (status === 'failed') {
      return { ...assessmentStatusCopy.failed, label: 'Needs attention' };
    }
  }
  return assessmentStatusCopy[status];
}

const emptyStatusCounts = (): Record<AssessmentProgressStatus, number> => ({
  not_started: 0,
  enrolled: 0,
  in_progress: 0,
  submitted: 0,
  in_review: 0,
  needs_revision: 0,
  failed: 0,
  earned: 0,
});

const statusWeight: Record<AssessmentProgressStatus, number> = {
  earned: 0,
  in_review: 1,
  in_progress: 2,
  submitted: 3,
  needs_revision: 4,
  failed: 5,
  enrolled: 6,
  not_started: 7,
};

export interface DeriveStatusOptions {
  passThreshold?: number | null;
  completionOnly?: boolean;
  hasEnrollment?: boolean;
  /** true when an open review task (pending|in_review) exists for the latest submission */
  reviewOpen?: boolean;
  /** set when the review task resolved */
  reviewDecision?: 'approved' | 'rejected' | 'returned' | null;
}

export function deriveAssessmentProgressStatus(
  latestSubmission: AssessmentProgressSubmission | null | undefined,
  options: DeriveStatusOptions | boolean = {},
): AssessmentProgressStatus {
  // Back-compat: old callers passed a `hasEnrollment` boolean as the 2nd arg.
  const opts: DeriveStatusOptions = typeof options === 'boolean' ? { hasEnrollment: options } : options;
  const status = latestSubmission?.status || null;
  const metadata = latestSubmission?.metadata || {};
  if (status === 'in_progress') return 'in_progress';
  if (status === 'grading') return 'submitted';
  if (status === 'graded' && metadata.credential_award_status === 'pending') return 'submitted';

  const heldForReview =
    opts.reviewOpen === true ||
    metadata.result_held_for_review === true ||
    status === 'pending_review' ||
    status === 'pending';

  if (opts.reviewDecision === 'rejected' || opts.reviewDecision === 'returned') return 'failed';
  // Held-for-review outranks any AI/auto score: a human must approve before it counts as passed.
  if (heldForReview && opts.reviewDecision !== 'approved') return 'in_review';
  if (status === 'failed' || status === 'returned') return 'failed';

  if (status === 'submitted' || status === 'graded') {
    if (opts.completionOnly === true) return 'earned';
    if (status === 'submitted') return 'submitted';
    if (opts.passThreshold == null) return 'failed';
    const raw = latestSubmission?.score_percent;
    const score = typeof raw === 'string' ? Number(raw) : raw;
    return typeof score === 'number' && Number.isFinite(score) && score >= Number(opts.passThreshold)
      ? 'earned'
      : 'failed';
  }

  return opts.hasEnrollment ? 'enrolled' : 'not_started';
}

export function isAssessmentProgressComplete(status: AssessmentProgressStatus) {
  return status === 'earned';
}

/** THE word list for assessment statuses, keyed by both the client vocabulary
 * and the raw server/DB tokens, so every surface prints the same words. Any
 * screen printing a status must resolve it here — never define labels locally. */
export const ASSESSMENT_STATUS_WORDS: Record<string, string> = {
  not_started: 'Not started',
  enrolled: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  grading: 'Submitted',
  pending: 'Out for review',
  pending_review: 'Out for review',
  pending_manual_review: 'Out for review',
  in_review: 'Out for review',
  needs_revision: 'Needs revision',
  returned: 'Returned',
  rejected: 'Did not pass',
  failed: 'Did not pass',
  earned: 'Complete',
  completed: 'Completed',
  graded: 'Completed',
  passed: 'Passed',
};

export function assessmentStatusWord(status: unknown): string | null {
  return ASSESSMENT_STATUS_WORDS[String(status || '')] ?? null;
}

function rollupStatus(items: CourseAssessmentProgressItem[]): AssessmentProgressStatus | 'untracked' {
  if (!items.length) return 'untracked';
  return [...items].sort((a, b) => statusWeight[a.status] - statusWeight[b.status])[0].status;
}

function getDashboardTabId(link: any): string {
  const settings = link?.settings || {};
  const tabId = settings.dashboardTabId || settings.dashboard_tab_id || link.dashboard_tab_id;
  return typeof tabId === 'string' && tabId.trim() ? tabId.trim() : 'overview';
}

/** Legacy bonus flag (settings.bonus === true on the step_assessments link).
 * Kept only so old data can still be recognized; it no longer excludes anything.
 * Every attached assessment is required, visible, and counted. */
export function isBonusAssessmentItem(item: { settings?: Record<string, unknown> | null } | null | undefined): boolean {
  return (item?.settings as any)?.bonus === true;
}

/** Every attached assessment must be done. Legacy requiredCompletions /
 * minimumRequired / groupMinimum settings are deliberately ignored: a lesson
 * with six assessments is done at 6/6, never 1/6. */
function getRequiredTarget(items: CourseAssessmentProgressItem[]): number {
  return items.length;
}

type TemplatePassInfo = { pass_threshold?: number | null; settings?: Record<string, unknown> | null };

/** Best-attempt status over an already-scoped submission list: once any attempt
 * earned, the answer stays 'earned' — an open retry never hides a completion. */
export function bestAttemptStatusForSubmissions(
  submissions: AssessmentProgressSubmission[],
  pass: TemplatePassInfo,
): AssessmentProgressStatus {
  const passThreshold = pass.pass_threshold ?? null;
  const completionOnly = (pass.settings as any)?.completionOnly === true;
  const anyEarned = (submissions || []).some(
    (s) => deriveAssessmentProgressStatus(s, { passThreshold, completionOnly }) === 'earned',
  );
  if (anyEarned) return 'earned';
  const latest = [...(submissions || [])].sort((a, b) => {
    const at = new Date(a.updated_at || a.graded_at || a.completed_at || a.created_at || 0).getTime();
    const bt = new Date(b.updated_at || b.graded_at || b.completed_at || b.created_at || 0).getTime();
    return bt - at;
  })[0] || null;
  return deriveAssessmentProgressStatus(latest, { passThreshold, completionOnly, hasEnrollment: true });
}

function bestAttemptStatus(
  submissions: AssessmentProgressSubmission[],
  templateId: string,
  stepId: string,
  pass: TemplatePassInfo,
): { status: AssessmentProgressStatus; latest: AssessmentProgressSubmission | null } {
  const mine = submissions.filter(
    (s) => s.template_id === templateId && (!s.step_id || s.step_id === stepId),
  );
  const passThreshold = pass.pass_threshold ?? null;
  const completionOnly = (pass.settings as any)?.completionOnly === true;
  const latest = [...mine].sort((a, b) => {
    const at = new Date(a.updated_at || a.graded_at || a.completed_at || a.created_at || 0).getTime();
    const bt = new Date(b.updated_at || b.graded_at || b.completed_at || b.created_at || 0).getTime();
    return bt - at;
  })[0] || null;
  const anyEarned = mine.some(
    (s) => deriveAssessmentProgressStatus(s, { passThreshold, completionOnly }) === 'earned',
  );
  if (anyEarned) return { status: 'earned', latest };
  return { status: deriveAssessmentProgressStatus(latest, { passThreshold, completionOnly, hasEnrollment: true }), latest };
}

export function buildCourseAssessmentProgress(params: {
  stepAssessments: any[];
  submissions: AssessmentProgressSubmission[];
  hasEnrollment?: boolean;
  templatesById?: Record<string, TemplatePassInfo>;
  attemptsByTemplateId?: Record<string, number | null>;
}): Record<string, CourseAssessmentRoadmapProgress> {
  const byRoadmap: Record<string, CourseAssessmentRoadmapProgress> = {};
  const byStep: Record<string, CourseAssessmentProgressItem[]> = {};
  const seen = new Set<string>();

  for (const link of params.stepAssessments || []) {
    const stepId = link.step_id || link.step?.id;
    const templateId = link.template_id || link.assessment_templates?.id;
    const roadmapId = link.step?.roadmap_id || link.roadmap?.id;
    if (!stepId || !templateId || !roadmapId) continue;

    const key = `${stepId}:${templateId}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const pass = params.templatesById?.[templateId] || {};
    const { status, latest: latestSubmission } = bestAttemptStatus(params.submissions || [], templateId, stepId, pass);
    const item: CourseAssessmentProgressItem = {
      id: link.id || key,
      stepId,
      templateId,
      dashboardTabId: getDashboardTabId(link),
      required: link.required_for_completion !== false,
      status,
      latestSubmission,
      attemptsRemaining: params.attemptsByTemplateId?.[templateId] ?? null,
      ...(link.settings ? { settings: link.settings } : {}),
      ...((link.assessment_templates?.settings?.embeddedWorksheet === true || pass.settings?.embeddedWorksheet === true)
        ? { embeddedWorksheet: true } : {}),
    };

    byStep[stepId] = [...(byStep[stepId] || []), item];

    if (!byRoadmap[roadmapId]) {
      byRoadmap[roadmapId] = {
        roadmapId,
        total: 0,
        completed: 0,
        percentage: 0,
        statusCounts: emptyStatusCounts(),
        steps: {},
      };
    }

    byRoadmap[roadmapId].total += 1;
    byRoadmap[roadmapId].completed += isAssessmentProgressComplete(status) ? 1 : 0;
    byRoadmap[roadmapId].statusCounts[status] += 1;
  }

  for (const [stepId, items] of Object.entries(byStep)) {
    const firstLink = (params.stepAssessments || []).find((link) => (link.step_id || link.step?.id) === stepId);
    const roadmapId = firstLink?.step?.roadmap_id || firstLink?.roadmap?.id;
    if (!roadmapId || !byRoadmap[roadmapId]) continue;
    const countedItems = items;
    const availableCompleted = countedItems.filter((item) => isAssessmentProgressComplete(item.status)).length;
    const availableTotal = countedItems.length;
    const requiredTarget = getRequiredTarget(countedItems);
    const completed = Math.min(availableCompleted, requiredTarget);
    const stepStatusCounts = emptyStatusCounts();
    for (const item of countedItems) {
      stepStatusCounts[item.status] += 1;
    }
    const tabs: Record<string, CourseAssessmentTabProgress> = {};

    for (const item of items) {
      const tabId = item.dashboardTabId || 'overview';
      tabs[tabId] = tabs[tabId] || {
        tabId,
        total: 0,
        completed: 0,
        requiredTarget: 1,
        isRequirementMet: false,
        percentage: 0,
        status: 'untracked',
        statusCounts: emptyStatusCounts(),
        items: [],
      };
      tabs[tabId].items.push(item);
      tabs[tabId].total += 1;
      tabs[tabId].completed += isAssessmentProgressComplete(item.status) ? 1 : 0;
      tabs[tabId].statusCounts[item.status] += 1;
    }

    for (const tab of Object.values(tabs)) {
      tab.requiredTarget = getRequiredTarget(tab.items);
      tab.completed = Math.min(tab.completed, tab.requiredTarget);
      tab.isRequirementMet = tab.requiredTarget > 0 && tab.completed >= tab.requiredTarget;
      tab.percentage = tab.requiredTarget > 0 ? Math.round((tab.completed / tab.requiredTarget) * 100) : 0;
      tab.status = rollupStatus(tab.items);
    }

    byRoadmap[roadmapId].steps[stepId] = {
      stepId,
      total: requiredTarget,
      completed,
      availableTotal,
      availableCompleted,
      requiredTarget,
      isRequirementMet: requiredTarget > 0 && completed >= requiredTarget,
      percentage: requiredTarget > 0 ? Math.round((completed / requiredTarget) * 100) : 0,
      status: rollupStatus(countedItems),
      statusCounts: stepStatusCounts,
      items,
      tabs,
    };
  }

  for (const roadmap of Object.values(byRoadmap)) {
    const stepProgress = Object.values(roadmap.steps);
    if (stepProgress.length > 0) {
      roadmap.total = stepProgress.reduce((sum, step) => sum + step.total, 0);
      roadmap.completed = stepProgress.reduce((sum, step) => sum + step.completed, 0);
    }
    roadmap.percentage = roadmap.total > 0 ? Math.round((roadmap.completed / roadmap.total) * 100) : 0;
  }

  return byRoadmap;
}
